import { useLingui } from "@lingui/react/macro";
import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";

import { IconButton } from "../../icon-button";

/**
 * Small inline affordance for copying a cell's value to the clipboard. Renders
 * a brand-primary copy icon with a "Copy to clipboard" tooltip; on click it briefly
 * swaps to a green check as confirmation, then reverts.
 */
export function CopyCellButton({ value }: { value: string }) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  return (
    <IconButton
      icon={copied ? Check : Copy}
      size={12}
      label={t`Copy to clipboard`}
      // Both halves: the base purple hover would otherwise repaint the green
      // confirmation check on hover.
      className={
        copied
          ? "text-success-foreground hover:text-success-foreground"
          : undefined
      }
      onClick={async (event) => {
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
        } catch (error) {
          // Clipboard can reject (denied permission, insecure context). Don't
          // flash the confirmation, and swallow the rejection so it doesn't
          // surface as an unhandled promise rejection.
          console.warn("Copy to clipboard failed", error);
        }
      }}
    />
  );
}
